import { Input } from "shared/components";
import { SelectImportance } from "./SelectImportance";
import {
  ApplicantSettings,
  CoSignerSettings,
  CombinedSettings
} from "../../../types";
import { FC } from "react";
import { cx } from "shared/utils";
import { ApplicantSettingsErrors } from "types/landlord/verificationTemplate";
import { LeastMostLabel } from "./LeastMostLabel";
import { SettingCard } from "../../../../../components/SettingCard";

type Props = {
  criteria?: CoSignerSettings | ApplicantSettings | CombinedSettings;
  onValueChange: (
    criteria: string,
    key: string,
    e:
      | React.ChangeEvent<HTMLInputElement>
      | React.ChangeEvent<HTMLSelectElement>
  ) => void;
  applyCriteria: boolean;
  readOnly?: boolean;
  applicantSettingsErrors?: ApplicantSettingsErrors | null;
};

export const IncomeCriteria: FC<Props> = ({
  criteria,
  onValueChange,
  applyCriteria,
  readOnly,
  applicantSettingsErrors
}) => {
  if (!criteria) return null;

  return (
    <SettingCard title="Income">
      <p className="mb-3 text-sm font-medium text-content-1000">
        Gross income-to-rent <span className="text-content-200">(group)</span>
      </p>
      <div className="mb-4 flex justify-between">
        <div className="flex gap-2">
          <Input
            id="gross_income_to_rent_ratio"
            type="number"
            noIntlNormalization
            className="max-w-[76px]"
            value={criteria.gross_income_to_rent_ratio?.value}
            onChange={(e) =>
              onValueChange("gross_income_to_rent_ratio", "value", e)
            }
            readOnly={!applyCriteria || readOnly}
            error={applicantSettingsErrors?.gross_income_to_rent_ratio}
          />
          <LeastMostLabel />
        </div>

        <SelectImportance
          importance={criteria.gross_income_to_rent_ratio?.importance}
          onChange={(e) =>
            onValueChange("gross_income_to_rent_ratio", "importance", e)
          }
          disabled={Boolean(!applyCriteria || readOnly)}
        />
      </div>
      {applicantSettingsErrors?.gross_income_to_rent_ratio ? (
        <p className="text-danger-500 text-xs">
          {applicantSettingsErrors.gross_income_to_rent_ratio}
        </p>
      ) : null}

      <div className="mb-4">
        <p className="mb-3 text-sm font-medium text-content-1000">
          Gross income <span className="text-content-200">(group)</span>
        </p>
        <div className="flex justify-between">
          <div className="flex gap-2">
            <Input
              type="money"
              className={cx(
                "max-w-[100px] !mb-0",
                applicantSettingsErrors?.gross_income
                  ? "text-danger-700 border-danger-300"
                  : ""
              )}
              value={criteria.gross_income?.value}
              id="gross_income"
              onChange={(e) => onValueChange("gross_income", "value", e)}
              readOnly={!applyCriteria || readOnly}
            />
            <LeastMostLabel />
          </div>

          <SelectImportance
            importance={criteria.gross_income?.importance}
            onChange={(e) => onValueChange("gross_income", "importance", e)}
            disabled={Boolean(!applyCriteria || readOnly)}
          />
        </div>
        {applicantSettingsErrors?.gross_income ? (
          <p className="text-danger-500 text-xs">
            {applicantSettingsErrors.gross_income}
          </p>
        ) : null}
      </div>

      <div className="mb-4">
        <p className="mb-3 text-sm font-medium text-content-1000">
          Net income <span className="text-content-200">(group)</span>
        </p>
        <div className="flex justify-between ">
          <div className="flex gap-2">
            <Input
              id="net_income"
              type="money"
              className={cx(
                "max-w-[100px] w-full",
                applicantSettingsErrors?.net_income
                  ? "text-danger-700 border-danger-300"
                  : ""
              )}
              value={criteria.net_income?.value}
              onChange={(e) => onValueChange("net_income", "value", e)}
              readOnly={!applyCriteria || readOnly}
            />
            <LeastMostLabel />
          </div>

          <SelectImportance
            importance={criteria.net_income?.importance}
            onChange={(e) => onValueChange("net_income", "importance", e)}
            disabled={Boolean(!applyCriteria || readOnly)}
          />
        </div>
        {applicantSettingsErrors?.net_income ? (
          <p className="text-danger-500 text-xs">
            {applicantSettingsErrors.net_income}
          </p>
        ) : null}
      </div>

      <p className="mb-3 text-sm font-medium text-content-1000">
        Debt to net income <span className="text-content-200">(group)</span>
      </p>
      <div className="flex justify-between">
        <div className="flex gap-2">
          <Input
            id="debt_to_net_income"
            type="percentage"
            className={cx(
              "max-w-[100px] w-full",
              applicantSettingsErrors?.debt_to_net_income
                ? "text-danger-700 border-danger-300"
                : ""
            )}
            value={criteria.debt_to_net_income?.value}
            onChange={(e) => {
              e.target.value = e.target.value.replace("%", "");
              onValueChange("debt_to_net_income", "value", e);
            }}
            readOnly={!applyCriteria || readOnly}
          />
          <LeastMostLabel most />
        </div>

        <SelectImportance
          importance={criteria.debt_to_net_income?.importance}
          onChange={(e) => onValueChange("debt_to_net_income", "importance", e)}
          disabled={Boolean(!applyCriteria || readOnly)}
        />
      </div>
      {applicantSettingsErrors?.debt_to_net_income ? (
        <p className="text-danger-500 text-xs">
          {applicantSettingsErrors.debt_to_net_income}
        </p>
      ) : null}
    </SettingCard>
  );
};
